import { ProductCatalog } from "types/productCatalog";
import { useFetcher } from "react-router-dom";
import { EbayMetadata } from "types/ebayMetadata";
import { useEffect } from "react";
import { ebayCategoryIdMap } from "pages/ProductCatalog/categoriesAttributes";
import { Autocomplete, Box, CircularProgress, Divider, MenuItem, TextField, Typography } from "@mui/material";
import { EbayAspectAutocomplete } from "components/ProductCatalogs/EbayMultiAspectAutocomplete";
import { camelCase, snakeCase } from "lodash";

const categories = Object.keys(ebayCategoryIdMap);
export function EbayCategoryFields({ product }: { product: ProductCatalog }) {
  const fetcher = useFetcher<{ metadata: EbayMetadata }>();

  useEffect(() => {
    if (!product.primaryCategory || product.primaryCategory.trim().length === 0) return;
    const categoryId = ebayCategoryIdMap[product.primaryCategory];
    if (fetcher.state === "idle" && !fetcher.data && categoryId) {
      fetcher.load(`/ebay-category-metadata/${categoryId}`);
    }
  }, [product.primaryCategory, fetcher]);

  return (
    <>
      <TextField
        label="Ebay Category"
        name="product_catalog[primary_category]"
        required={true}
        defaultValue={product.primaryCategory || ""}
        onChange={(event) => {
          const categoryId = ebayCategoryIdMap[event.target.value];
          fetcher.load(`/ebay-category-metadata/${categoryId}`);
        }}
        select
      >
        {categories.map((category) => (
          <MenuItem value={category} key={category}>
            {category}
          </MenuItem>
        ))}
      </TextField>
      {fetcher.data && (
        <>
          <Divider />
          <Typography variant="h6">Ebay Category Attributes</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
              gap: 3,
            }}
          >
            <Autocomplete
              freeSolo
              disableClearable={true}
              options={fetcher.data.metadata.condition.itemConditions.map((v) => v.conditionDescription)}
              defaultValue={product.extraData?.condition}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Condition"
                  name={`extra_attributes[condition]`}
                  required={fetcher.data?.metadata?.condition?.itemConditionRequired}
                  slotProps={{ input: { ...params.InputProps, type: "search" } }}
                />
              )}
            />
            {fetcher.data.metadata.aspects.map((aspect) => (
              <>
                {aspect.aspectValues ? (
                  <EbayAspectAutocomplete aspect={aspect} product={product} />
                ) : (
                  <TextField
                    key={aspect.localizedAspectName}
                    name={`extra_attributes[${snakeCase(aspect.localizedAspectName)}]`}
                    label={aspect.localizedAspectName}
                    required={aspect.aspectConstraint.aspectRequired}
                    defaultValue={product.extraData?.[camelCase(aspect.localizedAspectName)]}
                  />
                )}
              </>
            ))}
          </Box>
        </>
      )}
      {fetcher.state === "loading" && !fetcher.data && <CircularProgress size={24} sx={{ ml: 1 }} />}
    </>
  );
}
